import type * as PIXI from 'pixi.js';
import { assertBoolean, assertMaximum, assertMinimum } from '../validators';
import { Config } from '../video-data/config';
import type { DestroyOptions } from '../types';
import type { AspectRatio } from '../aspectRatioConfig';
import type {
    OpaqueVideoData,
    RawSceneData,
    RawSceneGroupData,
    RawVideoData,
} from '../video-data/types';
import type { ConcurrentTimeline } from '../libs/ConcurrentTimeline';
import type { TickerTimeline } from '../libs/TickerTimeline';
import type { Scene } from './scene/Scene';
import type { AddSceneSettings } from './scene/types';
import type { SceneGroupSettings } from './SceneGroup';
import { SceneGroup } from './SceneGroup';
import type { BaseSettings } from './TimeAwareBase';
import { TimeInvariantBase } from './TimeInvariantBase';
import type { LoadSettings } from './serializableMixin';
import type { Template } from './template/Template';
import type { Capabilities } from './Capabilities';

export type TraverseScenesCallback<T> = (
    scene: Scene,
    sceneIndex: number,
    sceneGroup: SceneGroup,
    sceneGroupIndex: number,
) => T;

type SceneGroupManagerSettings = {
    preserveAssetIds: boolean;
    enableHelpers: boolean;
    autoUpdate: boolean;
    autoDraw: boolean;
    disableCaptions?: boolean;
    sceneGroupsTimeline: ConcurrentTimeline;
    timeline: TickerTimeline;
    workspace: PIXI.Container;
    getAspectRatio: () => AspectRatio;
    template: Template;
    capabilities: Capabilities;
} & BaseSettings;

const countScenesOfBrandCategory = (scenesList: Scene[]): number => {
    return scenesList.reduce((acc, scene) => {
        return acc + (scene.sceneCategory === Config.CATEGORY_BRAND ? 1 : 0);
    }, 0);
};

/**
 * The SceneGroupManager stores scenes & scene groups, providing logic to add, move & remove SceneGroups.
 * SceneGroupManager is not exposed directly as an API (all methods are internal). Some methods are available
 * in Luminary to access sceneGroups (refer to class Luminary). All operations on scenes groups are also reflected in Luminary.
 * Example: Moving a SceneGroup will also move all of its scenes.
 */
export class SceneGroupManager extends TimeInvariantBase {
    override _classType = 'SceneGroupManager';
    override _name = 'SceneGroupManager';

    #sceneGroups: SceneGroup[] = [];
    #sceneGroupsById = new Map<string, SceneGroup>();

    #preserveAssetIds: boolean;
    #enableHelpers: boolean;
    #disableBrandingCheck = false;
    #disableSceneGroupOrderCheck = false;
    #autoDraw = false;
    #disableCaptions = false;
    #sceneGroupsTimeline: ConcurrentTimeline;
    #timeline: TickerTimeline;
    #workspace: PIXI.Container;
    #getAspectRatio: () => AspectRatio;
    #template: Template;
    #capabilities: Capabilities;

    _requiresBrandingScene = false;

    constructor(settings: SceneGroupManagerSettings) {
        super(settings);
        this.#preserveAssetIds = settings.preserveAssetIds;
        this.#enableHelpers = settings.enableHelpers;

        if (settings.hasOwnProperty('autoDraw')) {
            this.#autoDraw = settings.autoDraw;
        }
        if (settings.hasOwnProperty('autoUpdate')) {
            this.autoUpdate = settings.autoUpdate;
        }
        if (settings.hasOwnProperty('disableCaptions')) {
            this.#disableCaptions = settings.disableCaptions!;
        }
        this.#sceneGroupsTimeline = settings.sceneGroupsTimeline;
        this.#timeline = settings.timeline;
        this.#workspace = settings.workspace;
        this.#getAspectRatio = settings.getAspectRatio;
        this.#template = settings.template;
        this.#capabilities = settings.capabilities;
    }

    /**
     * Returns a list of scene objects in the order they're configured in the video.
     */
    get scenes(): Scene[] {
        return this.#sceneGroups.flatMap((sceneGroup) => sceneGroup.scenes);
    }

    /**
     * Adds a SceneGroup with one scene and returns both.
     */
    _addSceneGroup(settings: AddSceneSettings): { sceneGroup: SceneGroup; scene: Scene } {
        const { sceneGroup, scene } = this.#batchRun(() => {
            const sceneGroup = this.#createSceneGroup();
            const scene = sceneGroup.addScene(settings as AddSceneSettings);

            // we only know the sceneCategory from a scene once we added it
            // maybe we can use `_getSceneDesignConfig` here instead? it would
            // reduce the amount of work we have to do here
            const scenes = this.scenes;
            if (scene.sceneCategory === Config.CATEGORY_BRAND) {
                if (countScenesOfBrandCategory(scenes) > 1) {
                    throw new Error('Cannot add more than one branding scene.');
                }
            }
            this.#addSceneGroupToInternalState(sceneGroup);
            this.requiresUpdate = true;

            return { sceneGroup, scene };
        });

        this.#ensureSceneGroupsAreOrderedCorrectly();

        return { sceneGroup, scene };
    }

    #addSceneGroupToInternalState(sceneGroup: SceneGroup) {
        this.#sceneGroups.push(sceneGroup);
        this.#sceneGroupsById.set(sceneGroup.id, sceneGroup);
    }

    #createSceneGroup(settings?: Partial<SceneGroupSettings>) {
        const sceneGroup = new SceneGroup({
            sceneGroupManager: this,
            timeline: this.#timeline,
            preserveAssetIds: this.#preserveAssetIds,
            enableHelpers: this.#enableHelpers,
            autoDraw: this.autoDraw,
            fetchResources: this.fetchResources,
            disableCaptions: this.#disableCaptions,
            autoUpdate: this.autoUpdate,
            ...settings,
        });
        // Make sure the aspect ratio is correct, else it will need to be fixed during fixAndUpdate, which
        // will occur after a load. The result is children of the group will first load, and then trigger updates
        // thinking the aspect ratio has changed from their loaded state.
        sceneGroup._updateAspectRatio(this.#getAspectRatio());
        this.#workspace.addChild(sceneGroup._workspace._pixiContainer);
        this.#sceneGroupsTimeline.add(sceneGroup._timeline);
        return sceneGroup;
    }

    _updateAspectRatio(value: AspectRatio) {
        this.#batchRun(() => {
            this._sceneGroups.forEach((sceneGroup) => sceneGroup._updateAspectRatio(value));
        });
    }

    /**
     * Removes all scene groups, removing their scenes and freeing their resources.
     */
    _removeAllSceneGroups() {
        // disable the scene group timeline from update, since we're going to remove everything
        // from it anyway
        const initAutoUpdate = this.#sceneGroupsTimeline.autoUpdate;
        this.#sceneGroupsTimeline.autoUpdate = false;
        this.#batchRun(() => {
            const length = this.#sceneGroups.length;
            for (let i = 0; i < length; i++) {
                this._removeSceneGroup(this.#sceneGroups[0].id);
            }
        });
        this.#sceneGroupsTimeline.autoUpdate = initAutoUpdate;
    }

    /**
     * Removes all groups
     * This is internal, outside of luminary, use Luminary.clear.
     */
    override clear() {
        this._removeAllSceneGroups();
        this.#sceneGroupsById.clear();
    }

    _removeSceneGroup(sceneGroupId: string) {
        const sceneGroup = this.#sceneGroupsById.get(sceneGroupId);

        if (!sceneGroup) {
            throw new Error('Attempt to delete a SceneGroup which does not exist.');
        }

        const sceneGroupIndex = this.#sceneGroups.indexOf(sceneGroup);
        sceneGroup.destroy(true);
        this.#sceneGroups.splice(sceneGroupIndex, 1);
        this.#sceneGroupsById.delete(sceneGroupId);

        this.requiresUpdate = true;
    }

    get size() {
        return this.#sceneGroups.length;
    }

    _getSceneGroupIndex(id: string) {
        const sceneGroup = this.#sceneGroupsById.get(id);
        return this.#sceneGroups.indexOf(sceneGroup!);
    }

    _nextSceneGroup(id: string): SceneGroup | null {
        const size = this.size;
        const curIndex = this._getSceneGroupIndex(id);
        const nextIndex = curIndex + 1;
        if (nextIndex < size) {
            return this.#sceneGroups[nextIndex];
        }
        return null;
    }

    _prevSceneGroup(id: string): SceneGroup | null {
        const curIndex = this._getSceneGroupIndex(id);
        const prevIndex = curIndex - 1;
        if (prevIndex >= 0) {
            return this.#sceneGroups[prevIndex];
        }
        return null;
    }

    _updateBrandingScene() {
        if (this.#disableBrandingCheck) {
            return;
        }
        const brandingScene = this.scenes.find(
            (scene) => scene.sceneCategory === Config.CATEGORY_BRAND,
        );
        if (this._requiresBrandingScene) {
            if (brandingScene) {
                if (
                    Config.getTemplateIdFromSceneDesignId(brandingScene.sceneDesign) !==
                    this.#template.id
                ) {
                    // we need to re-create the branding scene under the current template
                    this.#removeSceneGroupForSceneIfPresent(brandingScene.id);
                } else {
                    this.#ensureSceneGroupsAreOrderedCorrectly();
                    return;
                }
            }
            if (this.scenes.length === 0) {
                // Don't add branding scenes to empty videos
                return;
            }
            this._addSceneGroup({ sceneDesignId: `${this.#template.id}:brand_1` });
        } else {
            if (brandingScene) {
                this.#removeSceneGroupForSceneIfPresent(brandingScene.id);
            }
        }
    }

    _checkAndFixBranding() {
        if (this.#disableBrandingCheck) {
            return;
        }
        this._requiresBrandingScene = !this.#capabilities.noBranding;
    }

    /**
     * Remove the scene group containing the scene (identified by its id)
     */
    #removeSceneGroupForSceneIfPresent(sceneId: string) {
        const sceneGroup = this._getSceneGroupForScene(sceneId);
        if (sceneGroup) {
            this._removeSceneGroup(sceneGroup.id);
        }
    }

    /**
     * Move a group (identified by its SceneGroupId) to a new index.
     * Since we use splice internally, this method is O(N).
     */
    _moveSceneGroupToIndex(sceneGroupId: string, newIndex: number, checkOutrosAndBrand = true) {
        assertMinimum(newIndex, 0);
        assertMaximum(newIndex, this.size - 1);

        const sceneGroupToMove = this.#sceneGroupsById.get(sceneGroupId)!;
        const currentIndex = this.#sceneGroups.indexOf(sceneGroupToMove);

        if (newIndex === currentIndex) {
            return;
        }
        if (
            checkOutrosAndBrand &&
            (sceneGroupToMove.isBrandingSceneGroup || sceneGroupToMove.isOutroSceneGroup)
        ) {
            throw new Error('Cannot move an outro or branding scene');
        }
        const outroScenesCount = this.#sceneGroups.reduce(
            (acc, sceneGroup) => (sceneGroup.isOutroSceneGroup ? acc + 1 : acc),
            0,
        );

        // Check if we are moving the group after an outro
        // Skip this check when we have multiple outros (this is normal when displaying outro previews)
        const firstOutroIndex = this.#sceneGroups.findIndex(
            (sceneGroup) => sceneGroup.isOutroSceneGroup,
        );
        if (
            checkOutrosAndBrand &&
            outroScenesCount === 1 &&
            newIndex >= firstOutroIndex &&
            !sceneGroupToMove.isBrandingSceneGroup
        ) {
            throw new Error("Can't move a scene after an outro scene.");
        }

        const firstBrandingIndex = this.#sceneGroups.findIndex(
            (sceneGroup) => sceneGroup.isBrandingSceneGroup,
        );
        if (checkOutrosAndBrand && firstBrandingIndex >= 0 && newIndex >= firstBrandingIndex) {
            throw new Error("Can't move a scene after a branding scene.");
        }

        // Move sceneGroups in Luminary
        this.#batchRun(() => {
            // Move scene group in this manager
            this.#sceneGroups.splice(currentIndex, 1);
            this.#sceneGroups.splice(newIndex, 0, sceneGroupToMove);
            this.#sceneGroupsTimeline.moveChild(currentIndex, newIndex);
        });

        this.requiresUpdate = true;
    }

    /**
     * scenes can be in any order, except:
     * if present, any branding scene must be at the end
     * if present, any outro scene must be at the end (before any branding scenes)
     * NOTE: it's possible to have MULTIPLE outro scenes (for generating scene previews, etc).  If we detect
     *       multiple, we'll skip any ordering logic.
     */
    #ensureSceneGroupsAreOrderedCorrectly = () => {
        const outroSceneGroups = this.#sceneGroups.filter(
            (sceneGroup) => sceneGroup.isOutroSceneGroup,
        );
        if (outroSceneGroups.length > 1) {
            // multiple outro scenes, skip logic!
            return;
        }
        if (this.#disableSceneGroupOrderCheck) {
            // Nothing else to check, sceneGroups scenes will mismatch luminary scenes while loading.
            // The checks will happen during fixBuildDraw.
            return;
        }

        const outroSceneGroup = outroSceneGroups[0];
        const brandingSceneGroup = this.#sceneGroups.find(
            (sceneGroup) => sceneGroup.isBrandingSceneGroup,
        );
        let desiredLastGroupIndex = this.size - 1;
        if (brandingSceneGroup) {
            if (brandingSceneGroup.index !== desiredLastGroupIndex) {
                this._moveSceneGroupToIndex(brandingSceneGroup.id, desiredLastGroupIndex, false);
            }
            desiredLastGroupIndex -= 1; // outro should be ahead of branding scene
        }
        if (outroSceneGroup && outroSceneGroup.index !== desiredLastGroupIndex) {
            this._moveSceneGroupToIndex(outroSceneGroup.id, desiredLastGroupIndex, false);
        }
    };

    /**
     * Returns the scene in the current Luminary instance with that ID. If no matching scene is found,
     * returns `undefined`.
     */
    _getSceneById(id: string): Scene | undefined {
        // O(1) lookup using the Map index in each SceneGroup
        for (const sceneGroup of this.#sceneGroups) {
            const scene = sceneGroup._scenesById.get(id);
            if (scene && !scene.destroyed) {
                return scene;
            }
        }
    }

    _getSceneGroupById(sceneGroupId: string): SceneGroup | undefined {
        const sceneGroup = this.#sceneGroupsById.get(sceneGroupId);
        return sceneGroup?.destroyed ? undefined : sceneGroup;
    }

    /**
     * Get the parent group of a scene.
     *
     * Warning: This is not so efficient and should not be called in a loop if
     * traverseScenes can be used instead. (traverseScenes also provides the parent group
     * to the callback at no performance cost).
     */
    _getSceneGroupForScene(sceneId: string) {
        for (let i = 0; i < this._sceneGroups.length; i++) {
            const sceneGroup = this._sceneGroups[i];
            for (let j = 0; j < sceneGroup.scenes.length; j++) {
                const scene = sceneGroup.scenes[j];
                if (scene.id === sceneId) {
                    return sceneGroup;
                }
            }
        }
        return null;
    }

    get _sceneGroups(): SceneGroup[] {
        return this.#sceneGroups;
    }

    _traverseScenes<T>(callback: TraverseScenesCallback<T>) {
        return this.#batchRun(() => {
            return this.#sceneGroups.map((sceneGroup, sceneGroupIndex) => {
                return sceneGroup.scenes.map((scene, sceneIndex) => {
                    return callback(scene, sceneIndex, sceneGroup, sceneGroupIndex);
                });
            });
        });
    }

    override set autoUpdate(value: boolean) {
        if (this.autoUpdate === value) {
            return;
        }
        assertBoolean(value);
        this.#sceneGroups.forEach((sceneGroup) => {
            sceneGroup.autoUpdate = value;
        });
        super.autoUpdate = value;
    }

    override get autoUpdate(): boolean {
        return super.autoUpdate;
    }

    set autoDraw(value: boolean) {
        if (this.#autoDraw === value) {
            return;
        }
        assertBoolean(value);
        this.#autoDraw = value;
        this.#sceneGroups.forEach((sceneGroup) => {
            sceneGroup.autoDraw = value;
        });
    }

    get autoDraw(): boolean {
        return this.#autoDraw;
    }

    override _checkAndFixProperties() {
        this._checkAndFixBranding();
    }

    get requiresRedraw() {
        return this._sceneGroups.some((group) => group.requiresRedraw);
    }

    override _update() {
        this._updateBrandingScene();
    }

    _slidesCustomSave() {
        return this.scenes.map((scene) => scene.save());
    }

    _slidesCustomLoad(_sceneData: RawSceneData[]) {
        // instead of loading the scene groups and then the scenes, we load them together in load;
    }

    _sceneGroupsCustomSave() {
        return this.#sceneGroups.map((group) => group.save());
    }

    _sceneGroupsCustomLoad(_sceneGroupsData: RawSceneGroupData[]) {
        // instead of loading the scene groups and then the scenes, we load them together in load
    }

    /**
     * Run tasks in batch by setting autoUpdate to false temporarily and by pausing any checks
     * (outro and branding).
     */
    #batchRun<T>(callback: () => T) {
        const initDisableSceneGroupOrderCheck = this.#disableSceneGroupOrderCheck;
        this.#disableSceneGroupOrderCheck = true;
        const initDisableBrandingCheck = this.#disableBrandingCheck;
        this.#disableBrandingCheck = true;
        const initAutoDraw = this.autoDraw;
        const initAutoUpdate = this.autoUpdate;
        this.autoDraw = false;
        this.autoUpdate = false;

        try {
            return callback();
        } finally {
            this.#disableSceneGroupOrderCheck = initDisableSceneGroupOrderCheck;
            this.#disableBrandingCheck = initDisableBrandingCheck;
            this.autoDraw = initAutoDraw;
            this.autoUpdate = initAutoUpdate;
        }
    }

    override get readonlyProperties(): string[] {
        // SceneGroupManager does not need to serialize id, name, classType
        return [];
    }

    override get propertiesToSerialize() {
        // Scene groups must be loaded before scenes since they are the parents of our slides
        return ['sceneGroups', 'slides'];
    }

    override load(dataObj: OpaqueVideoData, settings?: LoadSettings) {
        super.load(dataObj, settings);

        // Combined loading for scenes and scene groups, so we can load scenes and groups together
        // from start to finish.
        const sceneGroupsData = (dataObj as RawVideoData).sceneGroups as RawSceneGroupData[];
        const scenesData = (dataObj as RawVideoData).slides;
        sceneGroupsData.forEach((sceneGroupData) => {
            const sceneGroup = this.#createSceneGroup({
                id: sceneGroupData.id,
            });
            sceneGroup.load(sceneGroupData);
            this.#addSceneGroupToInternalState(sceneGroup);

            this.#batchRun(() => {
                scenesData.forEach((sceneData) => {
                    if (sceneData.sceneGroupId === sceneGroupData.id) {
                        sceneGroup.addScene({ data: sceneData });
                    }
                });
            });
        });
    }

    override destroy(options?: DestroyOptions) {
        super.destroy(options);
        this.#sceneGroups.forEach((sceneGroup) => sceneGroup.destroy(options));
        this.#sceneGroups = [];
        this.#sceneGroupsById.clear();
    }
}
